import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import {
    Card,
    Row,
    Col,
    Button,
    Input,
    Label,
} from 'reactstrap';
import Select from 'react-select';
import { useQuery } from 'react-query';
import DeletePopOver from 'Components/DeletePopOver';
import { queryKeyes } from 'shared/queryKeys';
import { nextPossibleEvents } from 'VesselMaster/vesselMaster.hooks';
import { customStyle } from 'shared/CommonCSS';
import Loading from 'Components/Loading';
import { EventPrecedence } from 'shared/constants';
import { RootState } from 'index';
import { useSelector } from 'react-redux';
import ErrorComponent from 'Components/ErrorComponent';
// import FormValuesDebug from 'utils/debugTools/FormValuesDebug';

interface ReportRow {
    id: number;
    event: string;
    date: string;
    noon: boolean;
    cii: boolean;
}

const InsertReportPopUp = () => {
    /** State variables */
    const [deletepopup, setDeletePopup] = useState(false);
    const [index, setIndex] = useState(0);
    const [eventId, setEventId] = useState(0);
    const [count, setCount] = useState(0);
    const { CurrentReport, PreviousReport, loading, error } = useSelector((state: RootState) => state.Reporting);
    const currentVesselReport = CurrentReport?.data?.vessel_reporting_information ?? null;
    const previousVesselReport = PreviousReport?.data?.vessel_reporting_information ?? null;
    /** State variables end */

    /** useQueries */
    /** Load reporting event options based on previous reporting event */
    const { data: reportingEvent, isLoading: reportingEventIsLoading } = useQuery(
        [queryKeyes.vessel.nextReportingEvent, eventId],
        async () => {
            if (eventId) {
                setCount(count + 1);
                return await nextPossibleEvents(eventId);
            }
            return { id: 0, event: "No Records found" };
        },
    );
    /** useQueries end */

    /** useEffect */
    useEffect(() => {
        if (previousVesselReport && previousVesselReport?.data?.length > 0) {
            if (currentVesselReport?.data?.vessel_reporting_information_id && currentVesselReport?.data?.vessel_reporting_information_id?.id > 0) {
                setEventId(currentVesselReport?.data?.vessel_reporting_information_id?.previous_event_other_than_noon);
            } else if (previousVesselReport?.data?.vessel_reporting_information_id?.event_precedence_id === EventPrecedence.NOON ||
                previousVesselReport?.data?.vessel_reporting_information_id.event_precedence_id === EventPrecedence.CII_ADJUSTMENT_EVENT) {
                setEventId(previousVesselReport?.data?.vessel_reporting_information_id?.previous_event_other_than_noon);
            } else {
                setEventId(previousVesselReport?.data?.vessel_reporting_information_id?.reporting_event);
            }
        }
        setCount(count + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentVesselReport, previousVesselReport]);
    /** useEffect end */

    const formik = useFormik<{ reports: ReportRow[] }>({
        initialValues: {
            reports: [{
                id: Date.now(),
                event: '',
                date: '',
                noon: false,
                cii: false,
            }],
        },
        onSubmit: () => { },
    });

    const InsertReport = () => {
        formik.setFieldValue('reports', [
            ...formik.values.reports,
            {
                id: Date.now(),
                event: '',
                date: '',
                noon: false,
                cii: false,
            },
        ]);
    };

    const deleteRow = (index: number) => {
        if (formik.values.reports.length === 1) return;
        const updatedReports = [...formik.values.reports];
        updatedReports.splice(index, 1);
        formik.setFieldValue('reports', updatedReports);
    };

    return (
        <div className='p-2' >
            {(loading || reportingEventIsLoading) ? <Loading message='Loading required data!' /> :
                error ? <ErrorComponent message='Error loading required data!' /> :
                    <React.Fragment>
                        <Row className="align-items-center" >
                            <Col lg={4}>
                                <Card className="p-2 w-100 shadow">
                                    <h5 className="pos-start mb-1">{currentVesselReport?.data?.vessel_reporting_information_id?.reporting_event_name} Report</h5>
                                    <p className="text-muted font-size-12 pos-start mb-0">
                                        {currentVesselReport?.data?.vessel_reporting_information_id?.local_time?.slice(0, 10)} {currentVesselReport?.data?.vessel_reporting_information_id?.local_time?.slice(11, 16)}
                                    </p>
                                </Card>
                            </Col>
                        </Row>
                        <Row className="mb-2 p-2 border-bottom border-top">
                            <Col md={4}><Label><b>Previous Event</b> {currentVesselReport?.data?.vessel_reporting_information_id?.reporting_event_name}</Label></Col>
                            <Col md={4}><Label><b>Date & Time</b> {currentVesselReport?.data?.vessel_reporting_information_id?.local_time}</Label></Col>
                        </Row>

                        <form onSubmit={formik.handleSubmit} >
                            {formik.values.reports.map((row, index) => (
                                <Row key={row.id} className="mb-3 align-items-end">
                                    <Col md={3}>
                                        <Label>Event {index + 1}</Label>
                                        <Select
                                            options={
                                                reportingEvent && reportingEvent[0]?.next_possible_events?.next_possible_events
                                                    .filter((item: any) => item.event_precedence_id !== EventPrecedence.CII_ADJUSTMENT_EVENT &&
                                                        item.event_precedence_id !== EventPrecedence.NOON)
                                            }
                                            // value={row.event ? { label: row.event, value: row.event } : null}
                                            onChange={(selectedOption: any) =>
                                                formik.setFieldValue(`reports[${index}].event`, selectedOption?.value || '')
                                            }
                                            styles={customStyle}
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <Label>Date & Time</Label>
                                        <Input
                                            onFocus={(e) => e.target.select()}
                                            type="date"
                                            name={`reports[${index}].date`}
                                            value={row.date}
                                            onBlur={formik.handleBlur}
                                            onChange={formik.handleChange}
                                            className="datetimepicker text-uppercase mt-0"
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>Combined with</Label>
                                        <div className="d-flex">
                                            <Label className="me-4 d-flex align-items-center">
                                                <Input
                                                    onFocus={(e) => e.target.select()}
                                                    type="checkbox"
                                                    name={`reports[${index}].noon`}
                                                    checked={row.noon}
                                                    onChange={(e) =>
                                                        formik.setFieldValue(`reports[${index}].noon`, e.target.checked)
                                                    }
                                                />
                                                <span className="ms-2">Noon</span>
                                            </Label>
                                            <Label className="d-flex align-items-center">
                                                <Input
                                                    onFocus={(e) => e.target.select()}
                                                    type="checkbox"
                                                    name={`reports[${index}].cii`}
                                                    checked={row.cii}
                                                    onChange={(e) =>
                                                        formik.setFieldValue(`reports[${index}].cii`, e.target.checked)
                                                    }
                                                />
                                                <span className="ms-2">CII Adjustment</span>
                                            </Label>
                                        </div>
                                    </Col>
                                    <Col md={1} className="text-center">
                                        {index > 0 && (
                                            <i
                                                className="dripicons-trash icon_s18 icon-danger"
                                                id={`delete-row-${index}`}
                                                onClick={() => {
                                                    setIndex(index);
                                                    setDeletePopup(true);
                                                }}
                                                title="Delete row"
                                            />
                                        )}
                                    </Col>
                                </Row>
                            ))}

                            <div className="d-flex mt-3 mb-3">
                                <Label
                                    className="link_color_blue mt-2 mb-0"
                                    type="button"
                                    onClick={InsertReport}
                                >
                                    <i className="bx bx-plus me-1"></i> Add Report
                                </Label>
                            </div>

                            <Row className="mb-2">
                                <Col className="d-flex justify-content-end">
                                    <Button
                                        color="success"
                                        type="submit"
                                        className="btn waves-effect btn-label waves-light me-2"
                                    >
                                        <i className="bx bx-check-double label-icon icon_s23 align-middle" /> Confirm
                                    </Button>
                                </Col>
                            </Row>
                            {/* <FormValuesDebug values={[formik.values]} /> */}
                        </form>

                        <Row className="mt-2 p-1 border-bottom border-top">
                            <Col md={4}><Label><b>Next Event</b> {currentVesselReport?.data?.vessel_reporting_information_id?.reporting_event_name}</Label></Col>
                            <Col md={4}><Label><b>Date & Time</b> {currentVesselReport?.data?.vessel_reporting_information_id?.local_time}</Label></Col>
                        </Row>

                        {deletepopup && (
                            <DeletePopOver
                                state={deletepopup}
                                setState={setDeletePopup}
                                message={'Are you sure you want to delete this Report?'}
                                target={`delete-row-${index}`}
                                onClick={() => deleteRow(index)}
                            />
                        )}
                    </React.Fragment>
            }
        </div>
    );
};

export default InsertReportPopUp;
